import React, { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Card, CardContent } from "./ui/card";
import { Progress } from "./ui/progress";
import { BookOpen, CheckCircle2, X, ArrowRight, ChevronDown, Loader2, Share, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * OnboardingWizard — 1e-login walkthrough.
 *
 * Toont handboek-secties (algemeen + rol-specifiek) met afvink per onderdeel.
 * Wordt getoond wanneer backend status.should_show=true (alleen echte email domains).
 * Op iOS Safari + niet-standalone → extra install-instructie prominent bovenaan.
 */

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isAndroid = () => /Android/.test(navigator.userAgent);

const sectieKey = (rol, s) => {
  const t = (s.titel || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `${rol}:${t}`;
};

export default function OnboardingWizard() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);         // {should_show, klaar, afgevinkt, totaal}
  const [handboek, setHandboek] = useState(null);     // {rol, algemeen, rol_specifiek}
  const [step, setStep] = useState(0);                // 0 = PWA install, 1..N = secties
  const [expanded, setExpanded] = useState(true);
  const [afvinkBusy, setAfvinkBusy] = useState(false);
  const [pwaEvent, setPwaEvent] = useState(null);

  // Fetch onboarding-status
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/handboek/onboarding/status");
        if (!mounted) return;
        setStatus(data);
        if (data.should_show) {
          const hb = await api.get("/handboek/mijn");
          if (!mounted) return;
          setHandboek(hb.data);
          setOpen(true);
        }
      } catch {/* silent — endpoint kan er nog niet zijn */}
    })();
    return () => { mounted = false; };
  }, []);

  // PWA beforeinstallprompt event ophalen (Chrome/Android)
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setPwaEvent(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Samengesteld secties-array (algemeen eerst, dan rol-specifiek)
  const secties = React.useMemo(() => {
    if (!handboek) return [];
    const algemeen = (handboek.algemeen || []).map(s => ({ ...s, rol: "algemeen" }));
    const rol_spec = (handboek.rol_specifiek || []).map(s => ({ ...s, rol: handboek.rol }));
    return [...algemeen, ...rol_spec];
  }, [handboek]);

  const totaalStappen = 1 + secties.length; // 0 = PWA, 1..N = secties, laatste = klaar
  const huidigeSectie = step > 0 && step <= secties.length ? secties[step - 1] : null;
  const isAfgevinkt = huidigeSectie
    ? (status?.afgevinkt || []).includes(sectieKey(huidigeSectie.rol, huidigeSectie))
    : false;

  const doAfvink = async () => {
    if (!huidigeSectie) return;
    const key = sectieKey(huidigeSectie.rol, huidigeSectie);
    setAfvinkBusy(true);
    try {
      const { data } = await api.post("/handboek/onboarding/vink", { sectie_key: key });
      setStatus(data);
    } catch (e) {
      toast.error("Afvinken mislukt");
    } finally {
      setAfvinkBusy(false);
    }
  };

  const doAfsluiten = async (skip = false) => {
    try {
      await api.post("/handboek/onboarding/afsluiten");
      if (skip) toast.info("Handboek later te vinden onder Kennis → Handboek");
      else toast.success("Onboarding voltooid — welkom bij BPTplus!");
    } catch {/* silent */}
    setOpen(false);
  };

  const doPwaInstall = async () => {
    if (!pwaEvent) return;
    pwaEvent.prompt();
    const choice = await pwaEvent.userChoice;
    if (choice.outcome === "accepted") {
      toast.success("BPTplus geïnstalleerd op je beginscherm");
    }
    setPwaEvent(null);
    setStep(1);
  };

  const volgende = () => setStep(s => Math.min(s + 1, totaalStappen));
  const vorige = () => setStep(s => Math.max(s - 1, 0));

  // Als backend niet zegt should_show → niet renderen
  if (!open || !status?.should_show) return null;

  // Progress
  const pctAfgevinkt = status.totaal > 0 ? Math.round((status.aantal_afgevinkt / status.totaal) * 100) : 0;
  const alleAfgevinkt = status.totaal > 0 && status.aantal_afgevinkt >= status.totaal;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && doAfsluiten(true)}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto p-0"
        data-testid="onboarding-wizard"
      >
        {/* Header met progress */}
        <div className="bg-gradient-to-br from-[#0D3B66] to-[#1E5A8A] text-white p-4 sm:p-6 rounded-t-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white text-lg sm:text-2xl">
              <BookOpen className="w-6 h-6" /> Welkom bij BPTplus
            </DialogTitle>
            <DialogDescription className="text-blue-100 text-sm">
              Neem het handboek 1x door — vink af terwijl je leest. Dit hoef je maar 1 keer te doen.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3">
            <div className="flex justify-between text-xs text-blue-100 mb-1">
              <span data-testid="onboarding-progress-text">
                Stap {step + 1} van {totaalStappen + 1} · Afgevinkt: {status.aantal_afgevinkt}/{status.totaal} ({pctAfgevinkt}%)
              </span>
              <button className="hover:text-white text-blue-200" onClick={() => doAfsluiten(true)}
                       data-testid="onboarding-skip-btn">Later doen <X className="w-3 h-3 inline" /></button>
            </div>
            <Progress value={pctAfgevinkt} className="h-2 bg-blue-900" />
          </div>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-4">
          {/* Stap 0: PWA install */}
          {step === 0 && (
            <div className="space-y-3" data-testid="onboarding-pwa-step">
              <h3 className="text-lg font-bold text-[#0D3B66] flex items-center gap-2">
                <Download className="w-5 h-5" /> Zet BPTplus op je beginscherm
              </h3>
              <p className="text-sm text-slate-700">
                Installeer BPTplus als app — sneller openen, push-meldingen, en werk offline op eerder geladen data.
              </p>
              {isStandalone() ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800">
                  <CheckCircle2 className="w-4 h-4 inline mr-1" />
                  Al geïnstalleerd — je gebruikt BPTplus al als app.
                </div>
              ) : pwaEvent ? (
                <Button onClick={doPwaInstall} className="bg-[#F26522] hover:bg-orange-700 text-white"
                        data-testid="onboarding-pwa-install-btn">
                  <Download className="w-4 h-4 mr-1" /> Installeer BPTplus nu
                </Button>
              ) : isIOS() ? (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
                  <b>iOS Safari</b>: tik onderaan op <Share className="w-4 h-4 inline" /> "Delen",
                  scroll naar beneden en kies <b>"Zet op beginscherm"</b>.
                </div>
              ) : isAndroid() ? (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
                  <b>Android Chrome</b>: tik op ⋮-menu rechtsboven en kies <b>"App installeren"</b>.
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm text-slate-700">
                  <b>Desktop</b>: in de adresbalk zie je een install-icoontje (⊕) — klik daar om BPTplus als app te installeren.
                </div>
              )}
            </div>
          )}

          {/* Stap 1..N: handboek-secties */}
          {huidigeSectie && (
            <div className="space-y-3" data-testid={`onboarding-section-${step}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-block bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase">
                  {huidigeSectie.rol === "algemeen" ? "Algemeen" : "Rol-specifiek"}
                </span>
                <h3 className="text-lg sm:text-xl font-bold text-[#0D3B66]">{huidigeSectie.titel}</h3>
              </div>
              <Card>
                <CardContent className="p-4 prose prose-sm max-w-none text-slate-800 whitespace-pre-wrap"
                              data-testid="onboarding-content">
                  {renderMarkdownLite(huidigeSectie.inhoud || "")}
                </CardContent>
              </Card>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={isAfgevinkt}
                  onCheckedChange={() => !isAfgevinkt && doAfvink()}
                  disabled={afvinkBusy || isAfgevinkt}
                  data-testid="onboarding-checkbox"
                />
                <span className={`text-sm ${isAfgevinkt ? "text-emerald-700 font-semibold" : "text-slate-700"}`}>
                  {isAfgevinkt ? "✓ Ik heb dit gelezen" : "Vink af als je dit hebt gelezen"}
                </span>
                {afvinkBusy && <Loader2 className="w-3 h-3 animate-spin" />}
              </label>
            </div>
          )}

          {/* Laatste stap: klaar-scherm */}
          {step === totaalStappen && (
            <div className="text-center py-6 space-y-3" data-testid="onboarding-done-step">
              <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-600" />
              <h3 className="text-2xl font-bold text-[#0D3B66]">Klaar!</h3>
              <p className="text-slate-700">
                Je hebt {status.aantal_afgevinkt} van {status.totaal} secties afgevinkt.
                {alleAfgevinkt && " Fantastisch — je bent volledig ingelezen!"}
              </p>
              <p className="text-xs text-slate-500">
                Het handboek blijft beschikbaar onder <b>Documenten & snelkoppelingen → Handboek</b>.
              </p>
            </div>
          )}
        </div>

        {/* Footer met navigatie */}
        <div className="border-t bg-slate-50 px-4 sm:px-6 py-3 flex items-center justify-between gap-2 rounded-b-lg sticky bottom-0">
          <Button
            variant="outline" size="sm"
            onClick={vorige}
            disabled={step === 0}
            data-testid="onboarding-prev-btn"
          >
            Terug
          </Button>
          <div className="text-xs text-slate-500">
            {step === 0 ? "PWA install" : step === totaalStappen ? "Voltooid" : `${step} / ${secties.length}`}
          </div>
          {step === totaalStappen ? (
            <Button size="sm" onClick={() => doAfsluiten(false)}
                     className="bg-emerald-600 hover:bg-emerald-700 text-white"
                     data-testid="onboarding-finish-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Afsluiten & starten
            </Button>
          ) : (
            <Button size="sm" onClick={volgende}
                     className="bg-[#0D3B66] hover:bg-[#08284b] text-white"
                     data-testid="onboarding-next-btn">
              Volgende <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Lichte markdown-render (bold, italic, kop, list, code). Genoeg voor handboek-content.
function renderMarkdownLite(text) {
  const lines = text.split("\n");
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(<ul key={out.length} className="list-disc pl-6 my-2 space-y-0.5">
        {listBuf.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(li) }} />)}
      </ul>);
      listBuf = [];
    }
  };
  const inline = (s) => s
    .replace(/`([^`]+)`/g, "<code class='bg-slate-100 rounded px-1 text-xs'>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>")
    .replace(/\*([^*]+)\*/g, "<i>$1</i>");
  for (const raw of lines) {
    const line = raw.replace(/\r$/, "");
    if (line.startsWith("### ")) { flushList(); out.push(<h4 key={out.length} className="font-bold text-slate-800 mt-3 mb-1">{line.slice(4)}</h4>); }
    else if (line.startsWith("## ")) { flushList(); out.push(<h3 key={out.length} className="font-bold text-lg text-[#0D3B66] mt-4 mb-2">{line.slice(3)}</h3>); }
    else if (line.startsWith("# ")) { flushList(); out.push(<h2 key={out.length} className="font-bold text-xl text-[#0D3B66] mt-4 mb-2">{line.slice(2)}</h2>); }
    else if (line.match(/^[-*]\s+/)) { listBuf.push(line.replace(/^[-*]\s+/, "")); }
    else if (line.match(/^\d+\.\s+/)) { flushList(); out.push(<div key={out.length} className="ml-4 my-0.5" dangerouslySetInnerHTML={{ __html: inline(line) }} />); }
    else if (line.trim() === "") { flushList(); out.push(<div key={out.length} className="h-2" />); }
    else { flushList(); out.push(<p key={out.length} className="my-1" dangerouslySetInnerHTML={{ __html: inline(line) }} />); }
  }
  flushList();
  return out;
}
